import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Sparkles, MessageCircle, Zap, Check, Copy, ArrowLeft, ArrowRight, Clock, CheckCircle2, XCircle, Loader2, Tag } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "8801314824998";

function whatsappUrl(text) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}

// ────────────────────────────────────────────────────────────
// Main dialog
// ────────────────────────────────────────────────────────────
export function BuyCreditsDialog({ open, onOpenChange, needed = null, available = null, reason = null }) {
  const [step, setStep] = useState(1); // 1: pick pkg, 2: pay + submit, 3: pending status
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [submittedTopup, setSubmittedTopup] = useState(null);
  const [promo, setPromo] = useState(null); // { code, bonus_credits, kind, value }

  const { data: config } = useQuery({
    queryKey: ["topup-config"],
    queryFn: async () => (await api.get("/topup/config")).data,
    enabled: open,
  });

  // Reset on close
  const handleOpenChange = (o) => {
    onOpenChange(o);
    if (!o) {
      setTimeout(() => {
        setStep(1);
        setSelectedPkg(null);
        setSubmittedTopup(null);
        setPromo(null);
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="buy-credits-dialog"
        className="p-0 gap-0 max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto bg-zinc-950/98 backdrop-blur-2xl border border-white/10 rounded-3xl"
      >
        <DialogTitle className="sr-only">Buy credits</DialogTitle>
        <DialogDescription className="sr-only">
          Choose a credit package, pay with bKash or Nagad, and submit your transaction for verification.
        </DialogDescription>
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <Step1PickPackage
                config={config}
                needed={needed}
                available={available}
                reason={reason}
                onPick={(pkg) => { setSelectedPkg(pkg); setStep(2); }}
                onClose={() => handleOpenChange(false)}
              />
            </motion.div>
          )}
          {step === 2 && selectedPkg && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <Step2PayAndSubmit
                config={config}
                pkg={selectedPkg}
                promo={promo}
                setPromo={setPromo}
                onBack={() => setStep(1)}
                onSubmitted={(topup) => { setSubmittedTopup(topup); setStep(3); }}
              />
            </motion.div>
          )}
          {step === 3 && submittedTopup && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <Step3Pending
                topup={submittedTopup}
                onDone={() => handleOpenChange(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ── Step 1: pick package ──
function Step1PickPackage({ config, needed, available, reason, onPick, onClose }) {
  const packages = config?.packages || [];
  return (
    <>
      <div className="relative h-28 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #D946EF 0%, #7C3AED 40%, #06B6D4 100%)" }} />
        <div className="relative h-full flex items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur border border-white/30 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" fill="currentColor" />
          </div>
        </div>
      </div>
      <div className="p-6 pt-5">
        <h2 className="font-display text-2xl font-extrabold tracking-tighter text-white text-center">Buy credits</h2>
        <p className="text-sm text-zinc-400 text-center mt-2">
          {reason || "Choose a package. Pay with bKash. Credits added after quick verification."}
          {needed !== null && available !== null && (
            <span className="block text-xs text-zinc-500 mt-1">
              Need <span className="text-white font-mono">{needed}</span> · Available <span className="text-white font-mono">{available}</span>
            </span>
          )}
        </p>

        <div className="mt-5 space-y-2">
          {packages.map((p) => (
            <button
              key={p.id}
              data-testid={`bkash-pkg-${p.id}`}
              onClick={() => onPick(p)}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-fuchsia-500/50 hover:bg-white/[0.06] transition-colors text-left"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center shrink-0">
                <span className="font-display font-black text-white text-base">৳</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-white text-base">{p.label}</span>
                  {p.highlight && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 uppercase tracking-wider">{p.highlight}</span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{p.credits.toLocaleString()} credits</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-white text-lg">৳{p.price_bdt.toLocaleString()}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">BDT</div>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-500 ml-1 shrink-0" />
            </button>
          ))}
        </div>

        {/* Or WhatsApp */}
        <div className="mt-5 pt-5 border-t border-white/5">
          <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Or contact us directly</div>
          <Button
            data-testid="buy-credits-whatsapp-btn"
            onClick={() => window.open(whatsappUrl("Hi! I'd like to buy AI Bazaar credits. My account email is:"), "_blank", "noopener,noreferrer")}
            className="w-full h-11 rounded-2xl font-semibold text-sm text-white hover:brightness-110"
            style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}
          >
            <WhatsAppIcon className="w-4 h-4 mr-2" />
            Message us on WhatsApp
          </Button>
        </div>

        <button data-testid="buy-credits-close" onClick={onClose}
          className="w-full mt-3 h-10 rounded-xl text-xs text-zinc-500 hover:text-white hover:bg-white/5 transition-colors">
          Maybe later
        </button>
      </div>
    </>
  );
}

// ── Step 2: pay + submit ──
function Step2PayAndSubmit({ config, pkg, promo, setPromo, onBack, onSubmitted }) {
  const [method, setMethod] = useState("bkash"); // 'bkash' | 'nagad'
  const [trx, setTrx] = useState("");
  const [sender, setSender] = useState("");
  const [note, setNote] = useState("");
  const [promoInput, setPromoInput] = useState(promo?.code || "");
  const [promoError, setPromoError] = useState("");

  const bkashNumber = config?.bkash_number || "01XXXXXXXXX";
  const nagadNumber = config?.nagad_number || "";
  const hasNagad = !!nagadNumber;
  const currentNumber = method === "nagad" ? nagadNumber : bkashNumber;
  const currentType = method === "nagad" ? (config?.nagad_type || "Personal") : (config?.bkash_type || "Personal");

  // Reset promo whenever the package changes (bonus depends on package.credits)
  React.useEffect(() => {
    if (promo && promo.package_id && promo.package_id !== pkg.id) {
      setPromo(null);
      setPromoInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg.id]);

  const applyPromo = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/promocodes/validate", {
        code: promoInput.trim().toUpperCase(),
        package_id: pkg.id,
      });
      return data;
    },
    onSuccess: (d) => {
      setPromo(d);
      setPromoError("");
      toast.success(d.message || `Promo ${d.code} applied`);
    },
    onError: (e) => {
      setPromo(null);
      setPromoError(e.response?.data?.detail || "Invalid promo code");
    },
  });

  const clearPromo = () => {
    setPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const submit = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/topup/request", {
        package_id: pkg.id,
        transaction_id: trx.trim().toUpperCase(),
        sender_number: sender.trim(),
        payment_method: method,
        note: note.trim() || null,
        promo_code: promo?.code || null,
      });
      return data.topup;
    },
    onSuccess: (t) => onSubmitted(t),
    onError: (e) => toast.error(e.response?.data?.detail || "Submission failed"),
  });

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const trxValid = /^[A-Za-z0-9]{6,32}$/.test(trx.trim());
  const senderValid = /^01\d{9}$/.test(sender.trim());
  const canSubmit = trxValid && senderValid && !submit.isPending;

  const methodLabel = method === "nagad" ? "Nagad" : "bKash";
  const bonus = promo?.bonus_credits || 0;
  const totalCredits = pkg.credits + bonus;
  const waMessage = `Hi! I've paid ৳${pkg.price_bdt} to ${currentNumber} via ${methodLabel} for the ${pkg.label} package (${pkg.credits} credits${bonus > 0 ? ` + ${bonus} promo` : ""}). TrxID: ${trx || "___"}. My sender number: ${sender || "___"}.`;

  const brandColor = method === "nagad"
    ? "from-orange-500/12 to-red-500/12 border-orange-500/25 text-orange-300"
    : "from-pink-500/12 to-fuchsia-500/12 border-pink-500/25 text-pink-300";

  return (
    <div className="p-6">
      <button onClick={onBack} data-testid="bkash-back" className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="text-[10px] uppercase tracking-[0.24em] text-cyan-400 mb-1">Step 1 of 2</div>
      <h2 className="font-display text-xl font-extrabold tracking-tighter text-white">Send money</h2>

      {/* Package summary */}
      <div className="mt-4 p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-between">
        <div>
          <div className="text-xs text-zinc-400">You&apos;ll get</div>
          <div className="font-display font-bold text-white">
            {totalCredits.toLocaleString()} credits · {pkg.label}
          </div>
          {bonus > 0 && (
            <div className="text-[11px] text-emerald-400 mt-0.5" data-testid="promo-bonus-hint">
              {pkg.credits.toLocaleString()} + <b>{bonus.toLocaleString()} bonus</b> from {promo.code}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-zinc-400">You pay</div>
          <div className="font-display font-black text-white text-lg">৳{pkg.price_bdt.toLocaleString()}</div>
        </div>
      </div>

      {/* Promo code entry */}
      <div className="mt-3">
        {promo ? (
          <div
            data-testid="promo-applied"
            className="flex items-center justify-between gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                <Check className="w-4 h-4 text-emerald-300" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white font-mono truncate">{promo.code}</div>
                <div className="text-[11px] text-emerald-300 truncate">{promo.message}</div>
              </div>
            </div>
            <button
              data-testid="promo-remove"
              onClick={clearPromo}
              className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white shrink-0"
            >
              Remove
            </button>
          </div>
        ) : (
          <details className="group" open={!!promoInput}>
            <summary className="list-none cursor-pointer flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors select-none">
              <Tag className="w-3.5 h-3.5" />
              <span>Have a promo code?</span>
              <span className="text-zinc-600 group-open:hidden">— add for bonus credits</span>
            </summary>
            <div className="mt-2 flex gap-2">
              <Input
                data-testid="promo-input"
                value={promoInput}
                onChange={(e) => {
                  setPromoInput(e.target.value.toUpperCase());
                  if (promoError) setPromoError("");
                }}
                placeholder="WELCOME50"
                className="h-11 bg-white/[0.04] border-white/10 font-mono tracking-wider uppercase focus-visible:border-cyan-500/50"
              />
              <Button
                data-testid="promo-apply"
                onClick={() => applyPromo.mutate()}
                disabled={promoInput.trim().length < 2 || applyPromo.isPending}
                variant="outline"
                className="h-11 rounded-xl border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-white px-4"
              >
                {applyPromo.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
            </div>
            {promoError && (
              <div data-testid="promo-error" className="mt-1.5 text-[11px] text-rose-400">
                {promoError}
              </div>
            )}
          </details>
        )}
      </div>

      {/* Payment method tabs */}
      {hasNagad && (
        <div className="mt-4 grid grid-cols-2 gap-2 p-1 rounded-2xl bg-white/[0.04] border border-white/10">
          <button
            data-testid="method-bkash"
            onClick={() => setMethod("bkash")}
            className={`h-11 rounded-xl text-sm font-semibold transition-colors ${
              method === "bkash" ? "bg-pink-500/20 text-pink-200 border border-pink-500/40" : "text-zinc-400 hover:text-white"
            }`}
          >
            bKash
          </button>
          <button
            data-testid="method-nagad"
            onClick={() => setMethod("nagad")}
            className={`h-11 rounded-xl text-sm font-semibold transition-colors ${
              method === "nagad" ? "bg-orange-500/20 text-orange-200 border border-orange-500/40" : "text-zinc-400 hover:text-white"
            }`}
          >
            Nagad
          </button>
        </div>
      )}

      {/* Payment instructions */}
      <div className={`mt-4 p-4 rounded-2xl bg-gradient-to-br ${brandColor} border`}>
        <div className="text-[10px] uppercase tracking-widest mb-2 opacity-80">{methodLabel} · {currentType}</div>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Send Money to</div>
            <div className="font-mono font-bold text-2xl text-white tracking-wide" data-testid="bkash-number">{currentNumber}</div>
          </div>
          <button data-testid="bkash-copy" onClick={() => copy(currentNumber, "Number")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <Copy className="w-4 h-4 text-white" />
          </button>
        </div>
        <ol className="mt-4 space-y-1.5 text-xs text-zinc-300 list-decimal list-inside">
          <li>Open {methodLabel} app → <b>Send Money</b></li>
          <li>Send exactly <b>৳{pkg.price_bdt}</b> to <b>{currentNumber}</b></li>
          <li>Copy the <b>TrxID</b> from {methodLabel} SMS{method === "bkash" ? " (e.g. 9M4H8N2K3P)" : ""}</li>
          <li>Paste it below → Confirm</li>
        </ol>
      </div>

      {/* Form */}
      <div className="mt-4 space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{methodLabel} TrxID</label>
          <Input
            data-testid="bkash-trxid"
            value={trx}
            onChange={(e) => setTrx(e.target.value.toUpperCase())}
            placeholder="e.g. 9M4H8N2K3P"
            className="mt-1 h-11 bg-white/[0.04] border-white/10 font-mono tracking-wider uppercase focus-visible:border-cyan-500/50"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Your {methodLabel} number (sender)</label>
          <Input
            data-testid="bkash-sender"
            value={sender}
            onChange={(e) => setSender(e.target.value.replace(/\D/g, ""))}
            placeholder="01XXXXXXXXX"
            className="mt-1 h-11 bg-white/[0.04] border-white/10 font-mono focus-visible:border-cyan-500/50"
          />
        </div>
        <details className="text-xs text-zinc-500">
          <summary className="cursor-pointer hover:text-zinc-300">Optional note</summary>
          <Textarea data-testid="bkash-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="Anything you'd like us to know"
            className="mt-2 bg-white/[0.04] border-white/10 focus-visible:border-cyan-500/50 text-sm" />
        </details>
      </div>

      <Button
        data-testid="bkash-submit"
        onClick={() => submit.mutate()}
        disabled={!canSubmit}
        className="btn-primary w-full h-12 rounded-2xl font-semibold mt-5"
      >
        {submit.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : <>Confirm payment <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>

      <div className="mt-3 flex items-center gap-2 justify-center text-[11px] text-zinc-500">
        <span>Faster approval:</span>
        <button
          data-testid="bkash-whatsapp-msg"
          onClick={() => window.open(whatsappUrl(waMessage), "_blank", "noopener,noreferrer")}
          className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
        >
          <WhatsAppIcon className="w-3 h-3" /> WhatsApp us
        </button>
      </div>
    </div>
  );
}

// ── Step 3: pending status ──
function Step3Pending({ topup, onDone }) {
  const { setUser } = useAuth();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["my-topup", topup.id],
    queryFn: async () => (await api.get("/topup/my")).data,
    enabled: !!topup?.id,
    refetchInterval: 4000,
  });
  const latest = (data?.topups || []).find((t) => t.id === topup.id) || topup;
  const status = latest.status;

  React.useEffect(() => {
    if (status === "approved") {
      api.get("/auth/me").then(({ data }) => setUser(data.user));
      qc.invalidateQueries({ queryKey: ["jobs"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-center mb-4">
        {status === "pending" && (
          <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Clock className="w-7 h-7 text-amber-400" />
          </div>
        )}
        {status === "approved" && (
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-emerald-400" />
          </div>
        )}
        {status === "rejected" && (
          <div className="w-16 h-16 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <XCircle className="w-7 h-7 text-red-400" />
          </div>
        )}
      </div>

      <h2 className="font-display text-2xl font-extrabold tracking-tighter text-white text-center" data-testid="topup-status-heading">
        {status === "pending" && "Waiting for verification"}
        {status === "approved" && "Credits added"}
        {status === "rejected" && "Payment rejected"}
      </h2>
      <p className="text-sm text-zinc-400 text-center mt-2">
        {status === "pending" && "We're verifying your payment in bKash. Usually takes a few minutes."}
        {status === "approved" && `${latest.credits.toLocaleString()} credits have been added to your account.`}
        {status === "rejected" && (latest.review_reason || "Please double-check the TrxID and contact support.")}
      </p>

      <div className="mt-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10 text-xs space-y-1.5">
        <Row label="Method" value={<span className="uppercase font-semibold">{latest.payment_method || "bkash"}</span>} />
        <Row label="Package" value={`${latest.package_label} · ${latest.credits} cr`} />
        <Row label="Amount" value={`৳${latest.price_bdt}`} />
        <Row label="TrxID" value={latest.transaction_id} mono />
        <Row label="Sender" value={latest.sender_number} mono />
        <Row label="Status" value={<StatusPill status={status} />} />
      </div>

      {status === "pending" && (
        <Button
          data-testid="topup-whatsapp-nudge"
          onClick={() => window.open(whatsappUrl(`Hi! Please verify my topup. TrxID: ${latest.transaction_id}, Amount: ৳${latest.price_bdt}`), "_blank", "noopener,noreferrer")}
          className="w-full h-12 rounded-2xl font-semibold text-white mt-4 hover:brightness-110"
          style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}
        >
          <WhatsAppIcon className="w-4 h-4 mr-2" />
          Nudge us on WhatsApp
        </Button>
      )}

      <Button
        data-testid="topup-done"
        onClick={onDone}
        variant="outline"
        className="w-full h-11 rounded-2xl mt-3 border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-white"
      >
        Close
      </Button>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={`text-white ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    rejected: "bg-red-500/15 text-red-300 border-red-500/30",
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${styles[status] || ""}`}>
      {status}
    </span>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.026 21.786a9.755 9.755 0 01-4.977-1.353L3.53 22.052l1.635-3.457a9.735 9.735 0 01-1.489-5.191c0-5.383 4.386-9.762 9.775-9.762 5.388 0 9.774 4.379 9.774 9.762s-4.386 9.762-9.774 9.762m8.31-18.07C18.14 1.523 15.229.312 12.026.312 5.416.312.077 5.646.077 12.255c0 2.113.554 4.176 1.605 5.995L0 24l5.9-1.548a11.884 11.884 0 005.688 1.446h.005c6.608 0 11.986-5.335 11.986-11.944-.001-3.196-1.242-6.11-3.243-8.238" />
    </svg>
  );
}
